import { CreateClientModal } from "@/components/clients/create-client-modal";
import { EditClientModal } from "@/components/clients/edit-client-modal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { clientsQueryOptions, useClientDeleteMutation } from "@/features/dashboard/api/queries/client-query";
import type { Client } from "@/features/dashboard/types/client";
import { queryClient } from "@/query-client";
import { exportToCSV, exportToPDF, exportToXLSX, prepareClientDataForExport } from "@/utils/export-data";
import { IconFileExport, IconFileSpreadsheet, IconFileTypeCsv, IconFileTypePdf, IconMail, IconPhone, IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

const columns: ColumnDef<Client>[] = [
	{
		id: "client_info",
		label: "Client",
		cellClassName: "min-w-[250px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<div className="flex flex-col space-y-1">
				<span className="font-semibold text-blue-600">{data.name}</span>
				{data.company && <span className="text-sm text-gray-600">{data.company}</span>}
				{data.email && (
					<div className="flex items-center text-sm text-gray-500">
						<IconMail className="h-3 w-3 mr-1 text-gray-400" />
						{data.email}
					</div>
				)}
				{data.phone && (
					<div className="flex items-center text-sm text-gray-500">
						<IconPhone className="h-3 w-3 mr-1 text-gray-400" />
						{data.phone}
					</div>
				)}
			</div>
		),
	},
	{
		id: "projects_count",
		label: "Projects",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span className="font-medium">{data.projects_count || 0}</span>,
	},
	{
		id: "sales_contracts_count",
		label: "Contracts",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span className="font-medium">{data.sales_contracts_count || 0}</span>,
	},
	{
		id: "status",
		label: "Status",
		cellClassName: "min-w-[100px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const statusColors = {
				active: "bg-green-100 text-green-800",
				inactive: "bg-gray-100 text-gray-800",
			};
			return (
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[data.status]}`}>
					{data.status.charAt(0).toUpperCase() + data.status.slice(1)}
				</span>
			);
		},
	},
	{
		id: "created_at",
		label: "Created",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.created_at ? new Date(data.created_at).toLocaleDateString() : "-"}</span>,
	},
];

export const Route = createFileRoute("/_dashboard/clients")({
	validateSearch: z.object({
		"filter[search]": z.string().optional(),
		per_page: z.number().optional(),
		status: z.string().optional(),
		page: z.number().nullable().optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [editingClient, setEditingClient] = useState<Client | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const deleteClientMutation = useClientDeleteMutation();

	const clientsQuery = useQuery(
		clientsQueryOptions({
			...search,
		}),
	);

	const handleEditClient = (client: Client) => {
		setEditingClient(client);
		setIsEditModalOpen(true);
	};

	const handleDeleteClient = async (client: Client) => {
		if (client.id) {
			await deleteClientMutation.mutateAsync(client.id.toString());
			await queryClient.invalidateQueries({
				queryKey: ["clients"],
			});
		}
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditingClient(null);
	};

	const handleEditSuccess = () => {
		clientsQuery.refetch();
	};

	const handleCreateClient = () => {
		setIsCreateModalOpen(true);
	};

	const handleCloseCreateModal = () => {
		setIsCreateModalOpen(false);
	};

	const handleCreateSuccess = () => {
		clientsQuery.refetch();
	};

	const handleSearch = async () => {
		await navigate({
			to: "/clients",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1, // Reset to first page when searching
			}),
		});
	};

	const handleSearchKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const clearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/clients",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
		if (!clients || clients.length === 0) {
			return;
		}

		const { headers, rows } = prepareClientDataForExport(clients);
		const filename = `clients_${new Date().toISOString().split("T")[0]}`;

		switch (format) {
			case "csv":
				exportToCSV({ headers, rows, filename });
				break;
			case "xlsx":
				exportToXLSX({ headers, rows, filename });
				break;
			case "pdf":
				await exportToPDF({ headers, rows, filename });
				break;
		}
	};

	const clients = clientsQuery.data?.data || [];
	const meta = clientsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 10,
		status_count: {},
	};

	if (clientsQuery.isLoading) {
		return (
			<Content pageName="Clients">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading clients...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Clients">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Clients</span>
						<span className="text-base text-foreground/70">Manage your client database</span>
					</div>
					<div className="flex gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline">
									<IconFileExport size={18} stroke={2} />
									<span>Export</span>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleExport("csv")} disabled={!clients || clients.length === 0}>
									<IconFileTypeCsv size={16} className="mr-2" />
									Export as CSV
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("xlsx")} disabled={!clients || clients.length === 0}>
									<IconFileSpreadsheet size={16} className="mr-2" />
									Export as Excel
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("pdf")} disabled={!clients || clients.length === 0}>
									<IconFileTypePdf size={16} className="mr-2" />
									Export as PDF
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<Button onClick={handleCreateClient}>
							<IconPlus size={18} stroke={2} />
							<span className="">Add New</span>
						</Button>
					</div>
				</div>
				<div className="flex flex-col w-full gap-4">
					{/* Search Bar */}
					<div className="flex items-center gap-2 w-full max-w-md">
						<div className="relative flex-1">
							<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
							<input
								type="text"
								placeholder="Search clients..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onKeyPress={handleSearchKeyPress}
								className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
							{searchTerm && (
								<button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
									×
								</button>
							)}
						</div>
						<Button onClick={handleSearch} variant="outline">
							Search
						</Button>
					</div>
					<DataTable
						className="border-collapse"
						data={clients}
						columns={columns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onEdit={handleEditClient}
						onDelete={(client) => {
							handleDeleteClient(client);
						}}
					/>
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/clients",
								search: (old) => ({
									...old,
									page,
								}),
							});
						}}
						initialPage={meta.current_page}
						pageCount={meta.last_page}
					/>
				</div>
			</div>
			<CreateClientModal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal} onSuccess={handleCreateSuccess} />
			{editingClient && <EditClientModal client={editingClient} isOpen={isEditModalOpen} onClose={handleCloseEditModal} onSuccess={handleEditSuccess} />}
		</Content>
	);
}
